import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ReviewState } from "./status-badge.js";
import { cn } from "../lib/utils.js";

const DOT: Record<ReviewState, string> = {
  queued: "var(--hold-queued)",
  running: "var(--hold-running)",
  done: "var(--hold-done)",
  failed: "var(--hold-failed)",
  skipped: "var(--hold-skipped)",
};

/**
 * A listing row: one review (or project) as a rounded card. `state` sets the
 * status dot rendered by StatusBadge inside; linked rows lift on hover.
 */
export function Strip({
  state,
  holderColor,
  to,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  state?: ReviewState;
  /** Explicit dot color for rows that are not reviews (e.g. projects). */
  holderColor?: string;
  to?: string;
  children: ReactNode;
}) {
  const dot = holderColor ?? (state ? DOT[state] : undefined);
  const classes = cn("tile block", className);
  const style = dot ? ({ "--dot": dot } as React.CSSProperties) : undefined;

  if (to) {
    return (
      <Link to={to} className={classes} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <div className={classes} style={style} {...props}>
      {children}
    </div>
  );
}

/** The row's fields: label over value, separated by whitespace, not rules. */
export function StripFields({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("flex flex-wrap items-stretch gap-1 sm:flex-nowrap", className)}>{children}</div>;
}

export function StripField({
  label,
  className,
  valueClassName,
  children,
}: {
  label: string;
  className?: string;
  /** Overrides the value wrapper's default truncate — e.g. wrapping chips. */
  valueClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col justify-center gap-0.5 px-3 py-3", className)}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className={cn("min-w-0 truncate text-sm font-medium", valueClassName)}>{children}</div>
    </div>
  );
}
